import * as DAPjs from 'dapjs';
import type { ProbeConnection, TargetInfo, FlashResult, FlashProgress, OptionBytes, FaultRegisters, RegisterMap, ProbeEvent } from './types';
import { CoreRegister } from './types';
import { TargetDB } from '@/target/TargetDB';

// ============================================================================
// WebUSBProbe — Mode 1: dapjs WebUSB implementation (spec section 4.2)
// ============================================================================

/**
 * WebUSB probe implementation using ARM dapjs.
 * Communicates directly with CMSIS-DAP debug probes via the browser's WebUSB API.
 */
export class WebUSBProbe implements ProbeConnection {
  private transport: DAPjs.WebUSB | null = null;
  private target: DAPjs.CortexM | null = null;
  private targetInfo: TargetInfo | null = null;
  private eventHandlers: Map<string, ((...args: unknown[]) => void)[]> = new Map();

  async connect(): Promise<TargetInfo> {
    // Request USB device via browser picker
    const device = await navigator.usb.requestDevice({
      filters: [
        { classCode: 0xFF, subclassCode: 0x00 },  // CMSIS-DAP v2 (bulk)
        { classCode: 0x03 },                        // CMSIS-DAP v1 (HID)
      ],
    });

    // Create DAPjs transport and target
    this.transport = new DAPjs.WebUSB(device);
    this.target = new DAPjs.CortexM(this.transport);

    await this.target.connect();
    await this.target.halt();

    // Read target IDCODE and identify MCU
    const idcode = await this.target.readDP(0x00);
    this.targetInfo = TargetDB.identify(idcode);

    this.emit('connected');
    return this.targetInfo;
  }

  async disconnect(): Promise<void> {
    if (this.target) {
      await this.target.disconnect();
      this.target = null;
      this.transport = null;
      this.targetInfo = null;
      this.emit('disconnected');
    }
  }

  isConnected(): boolean {
    return this.target !== null;
  }

  // ── Target Control ──────────────────────────────────────────

  async halt(): Promise<void> {
    this.ensureConnected();
    await this.target!.halt();
  }

  async resume(): Promise<void> {
    this.ensureConnected();
    await this.target!.resume();
  }

  async reset(halt: boolean = true): Promise<void> {
    this.ensureConnected();
    await this.target!.reset();
    if (halt) await this.target!.halt();
  }

  // ── Memory Access ───────────────────────────────────────────

  async readMemory(address: number, length: number): Promise<Uint8Array> {
    this.ensureConnected();
    const data = await this.target!.readBlock(address, length);
    return new Uint8Array(data);
  }

  async writeMemory(address: number, data: Uint8Array): Promise<void> {
    this.ensureConnected();
    // dapjs writeBlock expects Uint32Array — convert
    const u32 = new Uint32Array(data.buffer, data.byteOffset, Math.floor(data.byteLength / 4));
    await this.target!.writeBlock(address, u32);
  }

  // ── Register Access ─────────────────────────────────────────

  async readCoreRegister(reg: CoreRegister): Promise<number> {
    this.ensureConnected();
    return await this.target!.readCoreRegister(reg as number);
  }

  async writeCoreRegister(reg: CoreRegister, value: number): Promise<void> {
    this.ensureConnected();
    await this.target!.writeCoreRegister(reg as number, value);
  }

  async readAllRegisters(): Promise<RegisterMap> {
    const regs: RegisterMap = {};
    for (let i = 0; i <= 16; i++) {
      const name = CoreRegister[i] || `R${i}`;
      regs[name] = await this.readCoreRegister(i);
    }
    return regs;
  }

  // ── Flash Operations (stubbed — delegated to FlashEngine) ──

  async eraseFlash(_startAddr: number, _length: number): Promise<void> {
    this.ensureConnected();
    // TODO: Drive target flash controller registers for sector erase
    throw new Error('eraseFlash: use FlashEngine for complete flash operations');
  }

  async flashFirmware(
    _firmware: Uint8Array,
    _baseAddr: number,
    _onProgress?: (p: FlashProgress) => void
  ): Promise<FlashResult> {
    this.ensureConnected();
    // TODO: Implement via FlashEngine pipeline
    throw new Error('flashFirmware: use FlashEngine for complete flash operations');
  }

  // ── Readback Operations ─────────────────────────────────────

  async readFlash(_onProgress?: (percent: number) => void): Promise<Uint8Array> {
    this.ensureConnected();
    if (!this.targetInfo || this.targetInfo.flashSize === 0) {
      throw new Error('Unknown flash size — cannot read full flash');
    }
    return this.readRegion(0x08000000, this.targetInfo.flashSize);
  }

  async readRegion(address: number, length: number): Promise<Uint8Array> {
    return this.readMemory(address, length);
  }

  async readOptionBytes(): Promise<OptionBytes> {
    this.ensureConnected();
    // STM32 option bytes typically at 0x1FFFF800 (F1) or 0x1FFF7800 (F4)
    const raw = await this.readMemory(0x1FFFF800, 16);
    const rdpByte = raw[0];
    const rdpLevel = rdpByte === 0xAA ? 0 : rdpByte === 0xCC ? 2 : 1;
    return { rdpLevel: rdpLevel as 0 | 1 | 2, raw };
  }

  async readFaultRegisters(): Promise<FaultRegisters> {
    this.ensureConnected();
    return {
      cfsr: await this.readWord(0xE000ED28),
      hfsr: await this.readWord(0xE000ED2C),
      mmfar: await this.readWord(0xE000ED34),
      bfar: await this.readWord(0xE000ED38),
      dfsr: await this.readWord(0xE000ED30),
      pc: await this.readCoreRegister(CoreRegister.PC),
      lr: await this.readCoreRegister(CoreRegister.LR),
      sp: await this.readCoreRegister(CoreRegister.SP),
    };
  }

  // ── Events ──────────────────────────────────────────────────

  on(event: ProbeEvent, handler: (...args: unknown[]) => void): void {
    const handlers = this.eventHandlers.get(event) || [];
    handlers.push(handler);
    this.eventHandlers.set(event, handlers);
  }

  // ── Internal Helpers ────────────────────────────────────────

  private ensureConnected(): void {
    if (!this.target) {
      throw new Error('Probe not connected');
    }
  }

  private emit(event: ProbeEvent, ...args: unknown[]): void {
    const handlers = this.eventHandlers.get(event) || [];
    for (const handler of handlers) {
      handler(...args);
    }
  }

  private async readWord(address: number): Promise<number> {
    const data = await this.readMemory(address, 4);
    return data[0] | (data[1] << 8) | (data[2] << 16) | (data[3] << 24);
  }
}
